import API from './API';

// -----------------------------------------------------------------------------
//  Contabilità — totali di periodo
//
//  ⚠️ NON ESISTE UN ENDPOINT DI AGGREGAZIONE. @nestjsx/crud non fa SUM, quindi
//  ogni totale si ottiene caricando le righe del mese e sommandole qui.
//
//  Conseguenza da tenere presente: il backend tronca a MAX_CRUD_LIMIT senza
//  segnalarlo. Sommare righe troncate darebbe un totale SBAGLIATO e credibile,
//  che è il difetto peggiore su una pagina di contabilità. Per questo ogni
//  somma confronta le righe ricevute con il `total` dichiarato e, se non
//  combaciano, NON restituisce un numero: restituisce `{ truncated: true }` e
//  la pagina mostra un avviso al posto dell'importo.
// -----------------------------------------------------------------------------

// tetto del backend: oltre questo le righe verrebbero troncate in silenzio
const MAX_ROWS = 2000;

// Uno spostamento senza corse attive né contabilizzate — tutte eliminate, o
// nessuna mai inserita — in contabilità non esiste: senza questo filtro
// compariva come «0 tratte» a 0 €. Dal backend uno spostamento svuotato ora
// diventa ELIMINATA, ma quelli svuotati prima restano «programmati»: per
// questo si guarda alle corse e non allo stato.
const spostamentoAnnullato = (j) =>
  (j.routes || []).every((r) => r.status === 'ELIMINATA' && !r.is_billed);

// COD3 — quanto vale una corsa: l'imponibile corretto a mano da Simba, se
// c'è, altrimenti il prezzo congelato alla conferma.
export const prezzoCorsa = (r) =>
  r.imponibile_manuale !== null && r.imponibile_manuale !== undefined
    ? Number(r.imponibile_manuale)
    : Number(r.applied_price || 0);

// COD3 — le corse che valgono nel totale della giornata e il totale: quelle
// confermate o concluse, più le eliminate ma contabilizzate (il partner le
// paga comunque).
export function totaleSpostamento(j) {
  const tratte = (j.routes ?? []).filter(
    (r) => ['CONFERMATA', 'CONCLUSA'].includes(r.status) ||
      (r.status === 'ELIMINATA' && r.is_billed)
  );
  return {
    tratte_contabili: tratte.length,
    totale: tratte.reduce((s, r) => s + prezzoCorsa(r), 0),
    // Benzina fatta alle auto della giornata: rimborso spese a parte, fuori
    // dall'imponibile (vedi `benzina` nel COD3).
    carburante: tratte.reduce((s, r) => s + Number(r.carburante || 0), 0),
  };
}

// Mese contabile ITALIANO: i confini sono le mezzanotti di Roma, come nel
// backend. Le date a database sono UTC, quindi qui si manda l'istante, non il
// giorno.
const ROMA = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Rome',
  hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit',
});

// Mezzanotte italiana del primo del mese, come istante UTC.
function monthStart(year, month) {
  const naive = Date.UTC(year, month - 1, 1);
  let guess = naive;
  // Due passate: la prima toglie lo scarto del fuso, la seconda sistema i
  // mesi che cadono a ridosso del cambio d'ora.
  for (let i = 0; i < 2; i += 1) {
    const p = {};
    for (const { type, value } of ROMA.formatToParts(new Date(guess))) {
      if (type !== 'literal') p[type] = Number(value);
    }
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    guess = naive - (asUtc - guess);
  }
  return new Date(guess);
}

function monthRange(year, month) {
  const from = monthStart(year, month);
  const to = month === 12 ? monthStart(year + 1, 1) : monthStart(year, month + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

function normalize(response) {
  const body = response?.data;
  if (Array.isArray(body)) return { rows: body, total: body.length };
  return { rows: body?.data ?? [], total: body?.total ?? 0 };
}

// -----------------------------------------------------------------------------
//  ⚠️ `page` NON È FACOLTATIVO: senza, la difesa sul troncamento è finta.
//
//  @nestjsx/crud risponde in due forme diverse a seconda dei parametri:
//    - CON `page`  -> { data: [...], total: N }   ← il totale vero del server
//    - SENZA       -> [ ... ]                     ← un array nudo
//
//  Nel secondo caso `normalize` è costretta a dedurre `total = rows.length`,
//  quindi il confronto `total > rows.length` è sempre falso e un totale
//  TRONCATO passa per completo.
//
//  Questa funzione esiste per non doverselo ricordare a ogni chiamata: ogni
//  lettura paginata della contabilità passa di qui.
// -----------------------------------------------------------------------------
async function fetchPaged(path, { limit = MAX_ROWS, ...params } = {}) {
  const res = await API.get(path, { params: { ...params, page: 1, limit } });
  const { rows, total } = normalize(res);
  return { rows, total, truncated: total > rows.length };
}

// Somma `amountField` sulle righe del periodo, contando quante non sono ancora
// liquidate. Ritorna { amount, count, pending, truncated }.
async function sumOverPeriod(path, {
  dateField,
  amountField,
  pendingField = null,
  pendingWhenFalse = true,
  year,
  month,
  extraFilter = null,
} = {}) {
  const { from, to } = monthRange(year, month);
  const and = [{ [dateField]: { $gte: from, $lt: to } }];
  if (extraFilter) and.push(extraFilter);

  const { rows, total, truncated } = await fetchPaged(path, {
    s: JSON.stringify({ $and: and }),
  });

  // Il totale dichiarato non combacia con le righe ricevute: la somma sarebbe
  // parziale. Meglio nessun numero che un numero sbagliato.
  if (truncated) {
    return { amount: null, count: total, pending: null, truncated: true };
  }

  let amount = 0;
  let pending = 0;
  for (const r of rows) {
    amount += Number(r?.[amountField] ?? 0);
    if (pendingField) {
      const done = !!r[pendingField];
      if (pendingWhenFalse ? !done : done) pending += 1;
    }
  }
  return { amount, count: rows.length, pending: pendingField ? pending : null, truncated: false };
}

// Riusati dal report mensile del partner (`partnerReport.js`): stesso mese
// italiano e stessa difesa sul troncamento.
export { monthRange, fetchPaged };

// Id «di comodo» della pagina COD3 di tutti i partner (/contabilita/cod3/tutti).
export const COD3_TUTTI = 'tutti';

export default {
  monthRange,

  // COD1 — abbonamento mensile partner (tabella `fattura`).
  //
  // `fattura` NON riguarda i booking: `createFattura()` scorre le sedi con
  // `abbonamento > 0` e crea una riga con `imponibile = headquarter.abbonamento`
  // e `importo = 1,22 × abbonamento` (IVA). È quindi il canone mensile, cioè
  // il COD1.
  cod1(year, month) {
    return sumOverPeriod('/fattura', {
      dateField: 'data',
      amountField: 'importo',
      pendingField: 'liquidato',
      year,
      month,
    });
  },

  // COD2 — pacchetti valet venduti (tabella `minute`).
  //
  // Si filtra su `data_reg`, non su `data`: nella pagina COD2 esistente `data`
  // è un selettore compilato a mano dall'operatore (spesso vuoto), mentre
  // `data_reg` è la data di registrazione del pacchetto. Filtrando su `data`
  // il totale risultava zero anche con i pacchetti venduti.
  // Solo i pacchetti CONFERMATI dal cliente (`accepted`): quelli non ancora
  // accettati non sono una vendita. `esito` è invece lo stato di fatturazione.
  cod2(year, month) {
    return sumOverPeriod('/minute', {
      dateField: 'data_reg',
      amountField: 'tot_fattura',
      pendingField: 'esito',
      extraFilter: { accepted: { $eq: true } },
      year,
      month,
    });
  },

  // COD2 — pacchetti valet di un partner nel mese, per la pagina.
  async cod2Rows(partnerId, year, month) {
    const { from, to } = monthRange(year, month);
    const { rows, total, truncated } = await fetchPaged('/minute', {
      join: ['car', 'car.customer', 'partner'],
      s: JSON.stringify({
        $and: [
          { 'partner.id': { $eq: partnerId } },
          { accepted: { $eq: true } },
          { data_reg: { $gte: from, $lt: to } },
        ],
      }),
      sort: 'data_reg,ASC',
    });
    return { minuti: rows, truncated, total };
  },

  // COD4 — provvigioni. Una sola tabella con due origini: la provvigione è
  // legata a un `booking` oppure a un `minute` (pacchetto valet, creata da
  // `confirmCustomer`). Le righe `rejected` non sono un ricavo e si escludono.
  cod4(year, month) {
    return sumOverPeriod('/Provvigione', {
      dateField: 'data',
      amountField: 'importo',
      pendingField: 'liquidato',
      extraFilter: { type: { $eq: 'accepted' } },
      year,
      month,
    });
  },

  // COD4 — provvigioni di un partner nel mese, per la pagina di dettaglio.
  async cod4Rows(partnerId, year, month) {
    const { from, to } = monthRange(year, month);
    const { rows, total, truncated } = await fetchPaged('/provvigione', {
      join: ['booking', 'booking.customer', 'minute', 'minute.car'],
      s: JSON.stringify({
        $and: [
          { partnerId: { $eq: partnerId } },
          { type: { $eq: 'accepted' } },
          { data: { $gte: from, $lt: to } },
        ],
      }),
      sort: 'data,ASC',
    });
    return { provvigioni: rows, truncated, total };
  },

  // COD6 — compensi di driver e jolly.
  //
  // NON si somma `remuneration.importo`: quel campo non viene MAI scritto.
  // `calculateRemuneration()` salva solo i minuti lavorati (`minute_driver`,
  // `minute_jolly`) e lascia `importo` al default 0 — per questo il totale
  // usciva € 0,00 pur con decine di righe da liquidare.
  // Il compenso vero lo calcola il backend: ore × tariffa oraria dell'agenzia,
  // più rimborso km jolly e compenso notturno.
  async cod6(year, month) {
    const res = await API.get('/journey/driver-compensation', {
      params: { year, month },
    });
    const rows = res?.data?.rows ?? [];
    return {
      amount: Number(res?.data?.total ?? 0),
      count: rows.length,
      pending: null,
      truncated: false,
      raw: rows,
    };
  },

  // COD6 — righe complete per la pagina di dettaglio (una per driver).
  // Campi della riga: driver_id, driver, agency, hourly_rate, hours,
  // minuti_extra, corse_spostamenti, corse_prenotazioni, compenso, km_jolly,
  // rimborso_km, compenso_notturno, compenso_notturno_manuale, compenso_manuale,
  // totale, liquidato, liquidato_il, rif_fattura.
  //
  // `compenso` comprende `compenso_manuale`, cioè le gambe con un importo
  // deciso a mano (dal dettaglio della prenotazione o accettando la richiesta):
  // quelle non passano dalla tariffa oraria.
  async cod6Rows(year, month) {
    const res = await API.get('/journey/driver-compensation', {
      params: { year, month },
    });
    return {
      rows: res?.data?.rows ?? [],
      total: Number(res?.data?.total ?? 0),
      liquidato: Number(res?.data?.liquidato ?? 0),
      daLiquidare: Number(res?.data?.da_liquidare ?? 0),
      // `total` comprende TUTTE le voci di costo, trasferte incluse, e
      // `liquidato` comprende le trasferte segnate come pagate. `compensi` è
      // invece la sola parte che la liquidazione del mese congela.
      compensi: Number(res?.data?.compensi ?? 0),
      trasferte: Number(res?.data?.trasferte ?? 0),
      trasfertePagate: Number(res?.data?.trasferte_pagate ?? 0),
      trasferteDaPagare: Number(res?.data?.trasferte_da_pagare ?? 0),
    };
  },

  // COD6 — le corse che compongono il compenso di UN driver nel mese.
  // Stesso calcolo dei totali, quindi la somma delle corse è il suo compenso;
  // in un mese liquidato il totale è invece quello congelato nel registro, e
  // la risposta lo dichiara con `liquidato`.
  async cod6DriverRides(driverId, year, month) {
    const res = await API.get('/journey/driver-compensation/rides', {
      params: { driverId, year, month },
    });
    return {
      rides: res?.data?.rides ?? [],
      totale: Number(res?.data?.totale ?? 0),
      liquidato: !!res?.data?.liquidato,
      trasferte: Number(res?.data?.trasferte ?? 0),
      trasfertePagate: Number(res?.data?.trasferte_pagate ?? 0),
      trasferteDaPagare: Number(res?.data?.trasferte_da_pagare ?? 0),
    };
  },

  // COD6 — voci decise a mano su UNA corsa: compenso, rimborso trasferta (col
  // segno di pagato) e rimborso km del jolly. `kind` e `id` sono quelli della corsa nel
  // dettaglio del driver (`kind`, `ride_id`); `role` serve solo per le tratte
  // degli spostamenti interni, dove conducente e jolly stanno sulla stessa
  // riga.
  //
  // Si manda SOLO quello che si sta cambiando: un campo assente resta com'è,
  // `null` lo cancella (la trasferta sparisce, il rimborso km torna al calcolo
  // km × tariffa).
  cod6SetVociCorsa({ kind, id, role, compenso, trasferta, pagata, rimborsoKm, minuti }) {
    const num = (v) => (v === null || v === undefined ? null : Number(v));
    return API.patch('/journey/driver-compensation/corsa', {
      kind,
      id,
      ...(kind === 'route' ? { role: role === 'jolly' ? 'jolly' : 'driver' } : {}),
      // `null` riporta al calcolo ore × tariffa; assente = non si tocca.
      ...(compenso === undefined ? {} : { compenso: num(compenso) }),
      ...(trasferta === undefined ? {} : { trasferta: num(trasferta), pagata: !!pagata }),
      ...(rimborsoKm === undefined ? {} : { rimborso_km: num(rimborsoKm) }),
      // Minuti aggiuntivi del PASSEGGERO (quelli di conducente e jolly si
      // scrivono con l'assegnazione della tratta).
      ...(minuti === undefined ? {} : { minuti }),
    });
  },

  // COD6 — compensi aggiuntivi di un driver in un mese: voci che non nascono
  // da una corsa (rimborsi concordati, correzioni). Il server le somma al
  // totale del driver.
  async cod6Extras(year, month, driverId) {
    const res = await API.get('/journey/driver-compensation/extra', {
      params: { year, month, ...(driverId ? { driverId } : {}) },
    });
    return res?.data ?? [];
  },

  cod6AddExtra({ driverId, year, month, importo, descrizione }) {
    return API.post('/journey/driver-compensation/extra', {
      driverId, year, month, importo, descrizione: descrizione || undefined,
    });
  },

  cod6RemoveExtra(id) {
    return API.delete(`/journey/driver-compensation/extra/${id}`);
  },

  // COD6 — liquidazione del mese: congela gli importi nel registro
  // `remuneration`. Senza `driverIds` liquida tutti i driver con un compenso
  // da pagare; chi è già liquidato viene saltato dal backend.
  async cod6Liquidate(year, month, driverIds = [], rifFattura = null) {
    const body = { year, month };
    if (driverIds.length) body.driverIds = driverIds;
    // Testo, non numero: `Number('2026/0001')` sarebbe NaN, e uno zero
    // scritto a mano diventerebbe un riferimento vero invece di «nessuno».
    const rif = String(rifFattura ?? '').trim();
    if (rif) body.rif_fattura = rif;
    const res = await API.post('/journey/driver-compensation/liquidate', body);
    return res?.data ?? { liquidate: 0, totale: 0, saltati: [] };
  },

  // COD7 — provvigioni advisor. Qui il periodo è esplicito (period_year /
  // period_month), quindi non serve un intervallo di date.
  async cod7(year, month) {
    const res = await API.get('/advisor-commission', {
      params: {
        page: 1,
        limit: MAX_ROWS,
        s: JSON.stringify({
          $and: [{ period_year: { $eq: year } }, { period_month: { $eq: month } }],
        }),
      },
    });
    const { rows, total } = normalize(res);
    if (total > rows.length) {
      return { amount: null, count: total, pending: null, truncated: true };
    }
    let amount = 0;
    let pending = 0;
    for (const r of rows) {
      amount += Number(r?.commission_amount ?? 0);
      if (!r.liquidato) pending += 1;
    }
    return { amount, count: rows.length, pending, truncated: false };
  },

  // ------------------------------------------------------------------
  //  Partner che hanno effettivamente movimenti in un codice, nel periodo.
  //
  //  Serve alla scelta del partner: presentare tutti i partner obbliga a
  //  provarli a uno a uno per scoprire che nel mese non hanno niente.
  //
  //  Non esiste un "distinct partnerId" lato server (niente aggregazione),
  //  quindi si caricano le righe del periodo e si raccolgono gli id. È la
  //  stessa lettura che fa poi la pagina di dettaglio, con gli stessi filtri:
  //  se un partner compare qui, la sua pagina non sarà vuota.
  //
  //  ⚠️ SE LE RIGHE SONO TRONCATE non si filtra affatto (`truncated: true`):
  //  nascondere un partner perché la sua riga non è arrivata sarebbe peggio
  //  che mostrarne qualcuno di troppo.
  // ------------------------------------------------------------------
  async partnersWithData(source, year, month) {
    const { from, to } = monthRange(year, month);
    const period = (field) => ({ [field]: { $gte: from, $lt: to } });

    const CONF = {
      cod1: { path: '/fattura', and: [period('data')] },
      cod2: {
        path: '/minute',
        and: [period('data_reg'), { accepted: { $eq: true } }],
      },
      // La pagina COD3 mostra prenotazioni E spostamenti interni del mese
      // (`cod3Rows` + `cod3Journeys`): un partner che nel mese ha fatto solo
      // spostamenti deve comparire nell'elenco, altrimenti la sua pagina —
      // che non sarebbe vuota — non si raggiunge.
      cod3: {
        path: '/booking',
        and: [period('data'), { state: { $ne: 'canceled' } }],
        also: {
          path: '/journey',
          and: [period('date')],
          join: ['routes'],
          keep: (j) => !spostamentoAnnullato(j),
          label: ['spostamento', 'spostamenti'],
        },
      },
      cod4: {
        path: '/provvigione',
        and: [period('data'), { type: { $eq: 'accepted' } }],
      },
      // COD5 conta le TRANSAZIONI, non i booking: un partner con
      // prenotazioni ma senza pagamenti in app non ha nulla da mostrare.
      cod5: {
        path: '/booking',
        and: [period('data'), { state: { $ne: 'canceled' } }],
        join: ['tasks'],
        keep: (r) => (r.tasks?.length ?? 0) > 0,
      },
    };

    const conf = CONF[source];
    if (!conf) return { ids: null, counts: {}, truncated: false };

    const [main, also] = await Promise.all([
      fetchPaged(conf.path, {
        s: JSON.stringify({ $and: conf.and }),
        ...(conf.join ? { join: conf.join } : {}),
      }),
      conf.also
        ? fetchPaged(conf.also.path, {
          s: JSON.stringify({ $and: conf.also.and }),
          ...(conf.also.join ? { join: conf.also.join } : {}),
        })
        : Promise.resolve(null),
    ]);
    if (main.truncated || also?.truncated) return { ids: null, counts: {}, truncated: true };

    const counts = {};
    for (const r of main.rows) {
      if (conf.keep && !conf.keep(r)) continue;
      const pid = r.partnerId ?? r.partner?.id;
      if (!pid) continue;
      // COD5: si contano le transazioni, non le prenotazioni
      counts[pid] = (counts[pid] ?? 0) + (conf.keep ? r.tasks.length : 1);
    }

    // Seconda sorgente (COD3: spostamenti interni). Il conteggio resta a
    // parte, perché nell'elenco «3 prenotazioni» non deve contare anche gli
    // spostamenti: `extra[pid]` = { n, label }, con la dicitura già pronta.
    const extra = {};
    if (also) {
      for (const r of also.rows) {
        if (conf.also.keep && !conf.also.keep(r)) continue;
        const pid = r.partnerId ?? r.partner?.id;
        if (pid) extra[pid] = { n: (extra[pid]?.n ?? 0) + 1 };
      }
      const [uno, molti] = conf.also.label;
      for (const e of Object.values(extra)) e.label = `${e.n} ${e.n === 1 ? uno : molti}`;
    }
    const ids = new Set([...Object.keys(counts), ...Object.keys(extra)]);
    return { ids, counts, extra, truncated: false };
  },

  // COD1 — canoni di un partner nel mese, per la pagina di dettaglio.
  // Una riga per SEDE con abbonamento: `createFattura()` genera una fattura
  // per ogni sede con `abbonamento > 0`.
  async cod1Rows(partnerId, year, month) {
    const { from, to } = monthRange(year, month);
    const { rows, total, truncated } = await fetchPaged('/fattura', {
      join: ['headquarter'],
      s: JSON.stringify({
        $and: [
          { partnerId: { $eq: partnerId } },
          { data: { $gte: from, $lt: to } },
        ],
      }),
      sort: 'data,ASC',
    });
    return { fatture: rows, truncated, total };
  },

  // COD3 (hub) — riepilogo di tutte le prenotazioni del mese.
  //
  // Non esiste una tabella "riepilogo booking": il codice È una vista sui
  // booking. Il fatturato del mese è la somma di `tot_fattura`, lo stato di
  // incasso sta su `booking.liquidato`.
  async cod3(year, month) {
    const { from, to } = monthRange(year, month);
    const { rows, total, truncated } = await fetchPaged('/booking', {
      s: JSON.stringify({
        $and: [
          { state: { $ne: 'canceled' } },
          { data: { $gte: from, $lt: to } },
        ],
      }),
    });
    if (truncated) {
      return { amount: null, count: total, pending: null, truncated: true };
    }
    let amount = 0;
    let pending = 0;
    for (const b of rows) {
      // Il totale corretto a mano nel COD3, se c'è.
      amount += Number(b.totale_manuale ?? b.tot_fattura ?? 0);
      if (!b.liquidato) pending += 1;
    }
    return { amount, count: rows.length, pending, truncated: false };
  },

  // COD3 — imponibile e totale corretti a mano (solo Simba) su una prenotazione
  // o sulla giornata di uno spostamento interno. `null` torna al calcolo.
  cod3SetImporti(kind, id, { imponibile, totale }) {
    const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
    return API.patch(`/${kind === 'journey' ? 'journey' : 'booking'}/${id}/importi-cod3`, {
      imponibile_manuale: num(imponibile),
      totale_manuale: num(totale),
    });
  },

  // COD3 — la BENZINA fatta al veicolo del partner (solo Simba): sulla
  // prenotazione (`booking`) o sulla singola corsa di uno spostamento
  // (`route`). Rimborso spese a parte, senza IVA; `null` la toglie.
  cod3SetCarburante(kind, id, importo) {
    const carburante = importo === '' || importo === undefined ? null : importo;
    return kind === 'route'
      ? API.patch(`/journey/route/${id}/importo-cod3`, { carburante })
      : API.patch(`/booking/${id}/importi-cod3`, { carburante });
  },

  // COD3 — l'imponibile di una singola corsa corretto a mano (solo Simba).
  // `null` torna al prezzo congelato.
  cod3SetImportoCorsa(routeId, imponibile) {
    return API.patch(`/journey/route/${routeId}/importo-cod3`, {
      imponibile_manuale: imponibile === '' || imponibile === undefined ? null : imponibile,
    });
  },

  // COD3 — la giornata di uno spostamento segnata fatturata (solo Simba), o di
  // nuovo da fatturare con `liquidato: false`.
  cod3FatturaSpostamento(id, { rif_fattura, liquidato }) {
    return API.patch(`/journey/${id}/fatturazione`, { liquidato: !!liquidato, rif_fattura: rif_fattura ?? null });
  },

  // COD3 — prenotazioni di un partner nel mese, per la pagina di dettaglio.
  // `partnerId === COD3_TUTTI`: tutti i partner insieme (ricavi generali del
  // mese), col nome del partner su ogni riga.
  async cod3Rows(partnerId, year, month) {
    const { from, to } = monthRange(year, month);
    const tutti = partnerId === COD3_TUTTI;
    const { rows, total, truncated } = await fetchPaged('/booking', {
      // `tasks`: quanto il cliente ha già pagato in app su quella prenotazione.
      join: ['customer', 'car', 'headquarter', 'tasks', ...(tutti ? ['headquarter.partner'] : [])],
      s: JSON.stringify({
        $and: [
          ...(tutti ? [] : [{ partnerId: { $eq: partnerId } }]),
          { state: { $ne: 'canceled' } },
          { data: { $gte: from, $lt: to } },
        ],
      }),
      sort: 'data,ASC',
    });
    return { bookings: rows, truncated, total };
  },

  // COD3 — il DETTAGLIO delle prenotazioni scelte, per il PDF: le tratte con
  // orari, partenza e arrivo, km e minuti. La lista del COD3 non le carica
  // (sarebbe pesante per tutto il mese): si leggono solo al momento di
  // scaricare, a blocchi di id. Ritorna una mappa id → prenotazione.
  async cod3DettaglioPrenotazioni(ids) {
    const out = new Map();
    const unici = [...new Set(ids)].filter(Boolean);
    for (let i = 0; i < unici.length; i += 40) {
      const blocco = unici.slice(i, i + 40);
      // eslint-disable-next-line no-await-in-loop
      const { rows, truncated } = await fetchPaged('/booking', {
        join: [
          'pickup_bookings', 'pickup_bookings.point',
          'escort_bookings', 'escort_bookings.point_origin_escort', 'escort_bookings.point_destination_escort',
        ],
        s: JSON.stringify({ id: { $in: blocco } }),
      });
      if (truncated) throw new Error('Dettaglio delle prenotazioni incompleto');
      for (const b of rows) out.set(b.id, b);
    }
    return out;
  },

  // COD3 — i NOMI delle sedi (id → nome), per il PDF: le corse degli
  // spostamenti sanno solo l'id della sede da cui partono o a cui arrivano.
  async cod3NomiSedi(ids) {
    const out = new Map();
    const unici = [...new Set(ids)].filter(Boolean);
    for (let i = 0; i < unici.length; i += 40) {
      // eslint-disable-next-line no-await-in-loop
      const { rows } = await fetchPaged('/headquarter', {
        s: JSON.stringify({ id: { $in: unici.slice(i, i + 40) } }),
      });
      for (const h of rows) out.set(h.id, h.name || '');
    }
    return out;
  },

  // COD3 — spostamenti interni del partner nel mese, da mostrare insieme alle
  // prenotazioni (stessa pagina, filtro per servizio).
  //
  // Il totale è la somma dei prezzi delle tratte (`totaleSpostamento`), con la
  // stessa regola del riepilogo contabile; una corsa corretta a mano vale il
  // suo importo a mano.
  //
  // Le corse arrivano con partenza, arrivo, driver e jolly: la riga della
  // giornata si apre sul loro dettaglio. ⚠️ Niente join 'driver'/'jolly_driver'
  // a livello journey: collidono con quelli delle corse (vedi journeyAPI).
  async cod3Journeys(partnerId, year, month) {
    const { from, to } = monthRange(year, month);
    const tutti = partnerId === COD3_TUTTI;
    const { rows, total, truncated } = await fetchPaged('/journey', {
      join: [
        'routes', 'routes.startPoint', 'routes.endPoint', 'routes.driver', 'routes.jolly_driver',
        ...(tutti ? ['partner'] : []),
      ],
      s: JSON.stringify({
        $and: [
          ...(tutti ? [] : [{ partnerId: { $eq: partnerId } }]),
          { date: { $gte: from, $lt: to } },
        ],
      }),
      sort: 'date,ASC',
    });
    const journeys = rows
      .filter((j) => !spostamentoAnnullato(j))
      .map((j) => ({ ...j, ...totaleSpostamento(j) }));
    return { journeys, truncated, total };
  },

  // COD5 (hub) — transazioni in app di TUTTI i partner nel mese.
  //
  // `task` non ha un campo data: si passa dai booking del mese e si sommano i
  // costi dei task collegati. Vale la stessa difesa delle altre somme: se il
  // backend tronca, non si restituisce un numero.
  async cod5(year, month) {
    const { from, to } = monthRange(year, month);
    const { rows, total, truncated } = await fetchPaged('/booking', {
      join: ['tasks'],
      s: JSON.stringify({
        $and: [
          { state: { $ne: 'canceled' } },
          { data: { $gte: from, $lt: to } },
        ],
      }),
    });
    if (truncated) {
      return { amount: null, count: total, pending: null, truncated: true };
    }
    // ⚠️ Due campi diversi:
    //   `pay`      = la voce è PAGABILE in app (è una transazione in app);
    //   `accepted` = il cliente l'ha effettivamente PAGATA.
    // La card dice «incassi dei clienti tramite app», quindi somma solo le
    // pagate; le altre restano come «da incassare».
    let amount = 0;
    let count = 0;
    let pending = 0;
    for (const b of rows) {
      for (const t of b.tasks ?? []) {
        if (!t.pay) continue;
        count += 1;
        if (t.accepted) amount += Number(t.cost || 0);
        else pending += Number(t.cost || 0);
      }
    }
    return { amount, count, pending, truncated: false };
  },

  // COD5 — transazioni in app di un partner nel mese.
  //
  // `task` non ha un campo data: il periodo si ricava dal booking a cui è
  // collegato (`booking.data`). Si caricano quindi i booking del partner nel
  // mese con i loro task, e le transazioni si estraggono da lì.
  async cod5Rows(partnerId, year, month) {
    const { from, to } = monthRange(year, month);
    const { rows, total, truncated } = await fetchPaged('/booking', {
      join: ['customer', 'tasks', 'headquarter'],
      s: JSON.stringify({
        $and: [
          { partnerId: { $eq: partnerId } },
          { state: { $ne: 'canceled' } },
          { data: { $gte: from, $lt: to } },
        ],
      }),
      sort: 'data,ASC',
    });
    // stessa difesa delle somme: righe troncate darebbero un totale falso
    return { bookings: rows, truncated, total };
  },

  // COD7 — righe complete per la pagina, con l'advisor collegato.
  async cod7Rows(year, month) {
    const res = await API.get('/advisor-commission', {
      params: {
        page: 1,
        limit: MAX_ROWS,
        join: ['advisor'],
        s: JSON.stringify({
          $and: [{ period_year: { $eq: year } }, { period_month: { $eq: month } }],
        }),
      },
    });
    return normalize(res).rows;
  },

  // Ricalcolo del periodo. Il backend NON tocca le righe già liquidate e
  // riporta quante ne ha saltate: { period, computed, skipped_liquidated }.
  cod7Recalculate(year, month) {
    return API.post('/advisor-commission/recalculate', { year, month })
      .then((r) => r.data);
  },

  // Liquidazione di una provvigione. Risponde 409 se è già liquidata, e non
  // esiste una rotta per annullarla.
  cod7Liquidate(id, rifFattura) {
    return API.patch(`/advisor-commission/${id}/liquidate`, { rif_fattura: rifFattura });
  },

  // COD8 — affitto mezzi. Unico COD con un endpoint di resoconto dedicato:
  // le somme le fa il backend e restituisce
  //   { period, rows: [{ supplier_id, supplier, count, total, to_pay, paid }],
  //     totals: { count, total, to_pay, paid } }
  async cod8(year, month) {
    const d = (await API.get('/vehicle-rental/report', { params: { year, month } }))?.data;
    const rows = d?.rows ?? [];
    const totals = d?.totals ?? { count: 0, total: 0, to_pay: 0, paid: 0 };
    return {
      amount: Number(totals.total || 0),
      count: rows.length,
      // "da saldare" qui sono i FORNITORI con un residuo, non i noleggi
      pending: rows.filter((r) => Number(r.to_pay) > 0).length,
      truncated: false,
      rows,
      totals,
    };
  },

  // Singoli noleggi di un fornitore nel mese, per il dettaglio espandibile.
  // `booking.customer` non è fra i join consentiti: si può mostrare l'id del
  // booking, non il nome del cliente.
  // A differenza delle altre, questa non sommava nulla e non aveva alcuna
  // difesa: oltre il limite i noleggi sparivano dall'elenco in silenzio.
  // Ora dichiara il troncamento come tutte le altre.
  async cod8Rentals(supplierId, year, month) {
    const { from, to } = monthRange(year, month);
    const { rows, total, truncated } = await fetchPaged('/vehicle-rental', {
      join: ['supplier', 'booking'],
      s: JSON.stringify({
        $and: [
          { 'supplier.id': { $eq: supplierId } },
          { rental_date: { $gte: from.slice(0, 10), $lt: to.slice(0, 10) } },
        ],
      }),
      sort: 'rental_date,DESC',
      limit: 500,
    });
    return { rows, truncated, total };
  },

  // Saldo di un singolo noleggio. ⚠️ IRREVERSIBILE lato API: il controller non
  // espone un modo per tornare indietro.
  cod8Pay(rentalId, rifFattura) {
    return API.patch(`/vehicle-rental/${rentalId}/pay`, { rif_fattura: rifFattura });
  },

  // ---------------------------------------------------------------------
  //  Letture per le pagine COD1/COD3/COD4/COD5/COD6.
  //
  //  ⚠️ L'id che ricevono è quello del PARTNER, non della sede: il resoconto
  //  di un codice è per partner, con dentro tutte le sue sedi. Si filtra su
  //  `partnerId`, colonna denormalizzata e indicizzata su booking, fattura e
  //  provvigione.
  // ---------------------------------------------------------------------

  updateFattura(id, fattura) {
    return API.patch('/fattura/' + id, fattura);
  },

  // `config` passa opzioni axios, es. { skipErrorToast: true } quando la
  // pagina mostra da sé il messaggio del server.
  updateMinute(id, minute, config = {}) {
    return API.patch('/minute/' + id, minute, config);
  },

  getBookingCod4Minute(partnerId) {
    return API.get(
      `/provvigione?filter=minute.partner.id||$eq||${partnerId}&sort=minute.data,ASC&filter=liquidato||$eq||${false}`,
      {
        //return API.get(`/booking`, {
        params: {
          join: ['minute', 'minute.car', 'minute.partner'],
        },
        order: { data: 'ASC' },
      }
    );
  },

  getBookingCod4MinuteLiquidato(partnerId) {
    return API.get(
      `/provvigione?filter=minute.partner.id||$eq||${partnerId}&sort=minute.data,ASC&filter=liquidato||$eq||${true}`,
      {
        //return API.get(`/booking`, {
        params: {
          join: ['minute', 'minute.car', 'minute.partner'],
        },
        order: { data: 'ASC' },
      }
    );
  },

  updateFatturaProvvigione(id, data) {
    return API.patch('/provvigione/' + id, data);
  },
};
